import { useEffect } from "react";
import classes from "@/payment/PaymentMethods.module.scss";
import type { PaymentMethodsConfig } from "@/types/components/PaymentMethodsComponent";
import { withConfig } from "@/utils/withConfig";
import { selectPlatformMethodsAvailable, useDonationFormStore } from "@/stores";
import { usePaymentMethodsErrorsStore } from "@/payment/usePaymentMethodsErrorsStore";
import { usePaymentMethodsErrorsProcessor } from "@/payment/usePaymentMethodsErrorsProcessor";
import { useErrorProcessing } from "@/hooks/useErrorProcessing";
import { Alert } from "@/components/ui/Alert";
import { usePayment } from "@/payment/usePayment";
import { getDriver } from "@/payment/driverRegistry";
import { DirectDebitButton } from "@/payment/DirectDebitButton";
import { PaymentPopupBlockedError } from "@/payment/PaymentPopupBlockedError";
import { useTranslation } from "@/hooks/useTranslation";

type PaymentMethodsWithFormConfigProps = {
  config: PaymentMethodsConfig;
  onPaymentSuccess?: (paymentMethod: string) => void;
};

export function PaymentMethodsWithFormConfigInner({
  config,
  onPaymentSuccess,
}: Readonly<PaymentMethodsWithFormConfigProps>) {
  const {
    "credit-card": creditCard,
    paypal: payPal,
    "google-pay": googlePay,
    "apple-pay": applePay,
  } = config;

  const { session } = usePayment();
  const {
    donationAmount,
    donationType,
    setPaymentMethod,
    recurringDonationEnabled,
    recurringDirectDebitEnabled,
    recurringPaymentMethodsEnabled,
  } = useDonationFormStore();

  const t = useTranslation("payment_methods");
  const { errors, setErrors, resetErrors } = usePaymentMethodsErrorsStore();

  const errorProcessor = usePaymentMethodsErrorsProcessor();
  const { registerProcessor, unregisterProcessor } = useErrorProcessing();

  useEffect(() => {
    registerProcessor(errorProcessor);

    return () => {
      unregisterProcessor(errorProcessor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return null;

  const d = getDriver(session.gateway);

  const handleMethodSelect = (methodValue: string) => {
    setPaymentMethod(methodValue);
    resetErrors();
    onPaymentSuccess?.(methodValue);
  };

  const handlePayPalError = (error: Error) => {
    const message =
      error instanceof PaymentPopupBlockedError
        ? t("errors.paypal-popup-blocked")
        : t("errors.payment-error");

    setErrors({ payment: message as string });
  };

  const isMonthly = donationType === "monthly";
  const isRecurringType = donationType === "recurring";
  // #984/#985: the two per-form switches decide which payment types a recurring
  // donation offers; one-off donations always show the platform methods and
  // never Direct Debit.
  const showPlatformMethods = selectPlatformMethodsAvailable({
    donationType,
    recurringDonationEnabled,
    recurringPaymentMethodsEnabled,
  });
  const showDirectDebit = isRecurringType
    ? recurringDirectDebitEnabled
    : isMonthly && recurringDirectDebitEnabled;

  return (
    <div>
      {errors.payment && <Alert variant="error">{errors.payment}</Alert>}

      <div className={classes.grid}>
        {showPlatformMethods && creditCard.enabled && (
          <d.CreditCardButton
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handleMethodSelect(creditCard.value);
            }}
          />
        )}

        {showPlatformMethods && payPal.enabled && (
          <d.PayPalButton
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handleMethodSelect(payPal.value);
            }}
            onError={handlePayPalError}
          />
        )}

        {showPlatformMethods && googlePay.enabled && (
          <d.GooglePayButton
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handleMethodSelect(googlePay.value);
            }}
          />
        )}

        {showPlatformMethods && applePay.enabled && (
          <d.ApplePayButton
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handleMethodSelect(applePay.value);
            }}
          />
        )}

        {showDirectDebit && (
          <DirectDebitButton
            label="Direct Debit"
            onClick={() => {
              handleMethodSelect("DD");
            }}
          />
        )}
      </div>
    </div>
  );
}

export const PaymentMethodsWithFormConfig =
  withConfig<PaymentMethodsWithFormConfigProps>(
    PaymentMethodsWithFormConfigInner,
    "payment_methods",
  );
